import { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { ChevronDown, ChevronUp, Loader2, Plus, UserRound, X } from "lucide-react";
import { AutoComplete } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Paginator } from "primereact/paginator";
import { SelectButton } from "primereact/selectbutton";
import { TabPanel, TabView } from "primereact/tabview";

import {
    listCoachings,
    addCoaching,
    updateCoaching,
    addCoachingSession,
    getCoachingSessions,
    getBlockers,
    deleteBlockers,
    updateBlocker
} from "../../services/wisdomServices/growthService";
import { getAgentProjectsRequest } from "../../services/wisdomServices/projectService";
import { getTeamMembers } from "../../services/wisdomServices/managerService";
import { getSessionItem } from "../../utils/helper/session";
import { focusAreas } from "../../utils/helper/focusAreas";

import styles from "./styles.module.css";

const INITIAL_COACHING = {
    title: "",
    description: "",
    coacheeId: "",
    projectId: "",
    focusAreaId: 1,
    followupDate: "",
    status: "new"
};

const COACHING_STATUS_OPTIONS = [
    { label: "New", value: "new" },
    { label: "In Progress", value: "in-progress" },
    { label: "Closed", value: "closed" }
];

const MAX_TITLE_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 255;

const formatDate = (date) => {
    if (!date) return "";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) return "";

    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric"
    }).format(parsedDate);
};

const formatDateTime = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) return "-";

    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    }).format(parsedDate);
};

const getInitials = (name = "") => {
    return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase();
};

const formatStatus = (status = "") => {
    return status
        .replaceAll("_", " ")
        .replaceAll("-", " ")
        .split(" ")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
};

const getProjectName = (project) => {
    return project?.name || project?.projectName || `Project ${project?.id}`;
};

function Coaching() {
    const loggedUser = getSessionItem("User");
    const debounceRef = useRef(null);
    const coachingRequestRef = useRef(0);
    const [activeTab, setActiveTab] = useState(0);
    const [coachingItems, setCoachingItems] = useState([]);
    const [selectedCoachingStatus, setSelectedCoachingStatus] = useState("new");
    const [coachingPagination, setCoachingPagination] = useState({
        totalPages: 0,
        page: 1,
        limit: 10,
        totalLoaded: 0,
        totalResults: 0,
        hasNextPage: false,
        hasPreviousPage: false
    });
    const [expandedItems, setExpandedItems] = useState([]);
    const [sessionForms, setSessionForms] = useState({});
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [updatingId, setUpdatingId] = useState(null);
    const [newCoaching, setNewCoaching] = useState(INITIAL_COACHING);
    const [loggingSessionId, setLoggingSessionId] = useState(null);
    const [loadingSessions, setLoadingSessions] = useState({});
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [projects, setProjects] = useState([]);
    const [loadingProjects, setLoadingProjects] = useState(false);
    const [blockers, setBlockers] = useState([]);
    const [loadingBlockers, setLoadingBlockers] = useState(false);
    const [blockersLoaded, setBlockersLoaded] = useState(false);
    const [selectedBlockerStatus, setSelectedBlockerStatus] = useState("open");
    const blockerStatusOptions = [
        { label: "Open", value: "open" },
        { label: "In Progress", value: "in-progress" },
        { label: "Closed", value: "closed" }
    ];
    const [selectedBlocker, setSelectedBlocker] = useState(null);
    const [showEditBlocker, setShowEditBlocker] = useState(false);
    const [savingBlocker, setSavingBlocker] = useState(false);
    const [deletingBlockerId, setDeletingBlockerId] = useState(null);

    const [blockerForm, setBlockerForm] = useState({
        title: "",
        description: "",
        severity: null,
        ownerId: null,
        ownerName: "",
        status: "open"
    });

    const blockerSeverityOptions = [
        { label: "Low", value: "low" },
        { label: "Medium", value: "medium" },
        { label: "High", value: "high" },
        { label: "Urgent", value: "urgent" }
    ];

    useEffect(() => {
        loadCoachings();

        return () => {
            coachingRequestRef.current += 1;

            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    // =====================================================
    // COACHING REQUESTS
    // =====================================================

    const loadCoachings = async (
        page = 1,
        limit = 10,
        status = "new"
    ) => {
        const requestId = ++coachingRequestRef.current;

        try {
            setLoading(true);

            const response = await listCoachings({
                page,
                limit,
                status
            });

            if (requestId !== coachingRequestRef.current) return;

            const items = (response?.data || []).map((item) => ({
                ...item,
                sessions: item.sessions || []
            }));

            const pagination = response?.pagination || {};
            const totalResults = Number(pagination.totalResults ?? 0);
            const totalPages = Math.ceil(totalResults / limit);

            // An item may have moved to another status on the last row of a page.
            if (totalResults > 0 && page > totalPages) {
                await loadCoachings(totalPages, limit, status);
                return;
            }

            setCoachingItems(items);
            setCoachingPagination({
                totalPages: pagination.totalPages ?? totalPages,
                page: pagination.page ?? page,
                limit: pagination.limit ?? limit,
                totalLoaded: pagination.totalLoaded ?? items.length,
                totalResults,
                hasNextPage: pagination.hasNextPage ?? page < totalPages,
                hasPreviousPage: pagination.hasPreviousPage ?? page > 1
            });
            setExpandedItems([]);
        } catch (error) {
            if (requestId !== coachingRequestRef.current) return;

            console.error("Error loading coachings:", error);
            console.error("API response:", error?.response?.data);

            setCoachingItems([]);
        } finally {
            if (requestId === coachingRequestRef.current) {
                setLoading(false);
            }
        }
    };

    const handleCoachingStatusChange = (status) => {
        if (!status || status === selectedCoachingStatus) return;

        setSelectedCoachingStatus(status);
        void loadCoachings(1, coachingPagination.limit, status);
    };

    const handleCoachingPageChange = (event) => {
        void loadCoachings(
            event.page + 1,
            event.rows,
            selectedCoachingStatus
        );
    };

    const loadCoachingSessions = async (coachingId) => {
        try {
            setLoadingSessions((previous) => ({
                ...previous,
                [coachingId]: true
            }));

            const response = await getCoachingSessions(
                coachingId,
                1,
                50
            );

            setCoachingItems((previous) =>
                previous.map((item) =>
                    item.id === coachingId
                        ? {
                            ...item,
                            sessions: response?.data || []
                        }
                        : item
                )
            );
        } catch (error) {
            console.error("Error loading coaching sessions:", error);
            console.error("API response:", error?.response?.data);
        } finally {
            setLoadingSessions((previous) => ({
                ...previous,
                [coachingId]: false
            }));
        }
    };

    // =====================================================
    // BLOCKERS REQUEST
    // API FILTERS BASED ON MANAGER TOKEN
    // =====================================================

    
    const loadBlockers = async (status = selectedBlockerStatus) => {
        try {
            setLoadingBlockers(true);
            const response = await getBlockers({ status });
            const items = Array.isArray(response) ? response : response?.data ?? [];
            setBlockers(items);
            setBlockersLoaded(true);
        } catch (error) {
            console.error("Error loading blockers:", error);
            console.error("API response:", error?.response?.data);
            setBlockers([]);
        } finally {
            setLoadingBlockers(false);
        }
    };

    const handleBlockerStatusChange = async (status) => {
        setSelectedBlockerStatus(status);
        await loadBlockers(status);
    };

    const openEditBlocker = (blocker) => {
        setSelectedBlocker(blocker);

        setBlockerForm({
            title: blocker.title ?? "",
            description: blocker.description ?? "",
            severity: blocker.severity ?? null,
            ownerId: blocker.ownerId ?? null,
            ownerName: blocker.ownerName ?? "",
            status: blocker.status ?? "open"
        });

        setShowEditBlocker(true);
    };

    const closeEditBlocker = () => {
        if (savingBlocker) return;

        setShowEditBlocker(false);
        setSelectedBlocker(null);

        setBlockerForm({
            title: "",
            description: "",
            severity: null,
            ownerId: null,
            ownerName: ""
        });
    };

    const updateBlockerField = (field, value) => {
        setBlockerForm((previous) => ({
            ...previous,
            [field]: value
        }));
    };

    const handleUpdateBlocker = async () => {
        if (
            !selectedBlocker?.id ||
            !blockerForm.title.trim() ||
            !blockerForm.description.trim() ||
            !blockerForm.severity ||
            !blockerForm.ownerId
        ) {
            return;
        }

        try {
            setSavingBlocker(true);

            const payload = {
                title: blockerForm.title.trim(),
                description: blockerForm.description.trim(),
                severity: blockerForm.severity,
                ownerId: blockerForm.ownerId,
                status: blockerForm.status
            };

            const response = await updateBlocker(
                selectedBlocker.id,
                payload
            );

            const updatedBlocker =
                response?.data ?? response ?? {};

            setBlockers((previous) =>
                previous.map((blocker) =>
                    blocker.id === selectedBlocker.id
                        ? {
                            ...blocker,
                            ...updatedBlocker,
                            ...payload
                        }
                        : blocker
                )
            );

            closeEditBlocker();
            await loadBlockers();
        } catch (error) {
            console.error("Error updating blocker:", error);
            console.error(
                "API response:",
                error?.response?.data
            );
        } finally {
            setSavingBlocker(false);
        }
    };

    const handleDeleteBlocker = async (blocker) => {
        try {
            setDeletingBlockerId(blocker.id);

            await deleteBlockers(blocker.id);

            setBlockers((previous) =>
                previous.map((blocker) =>
                    blocker.id === selectedBlocker.id
                        ? {
                            ...blocker,
                            ...(response?.data ?? response ?? {}),
                            ...payload,
                            ownerName: blockerForm.ownerName
                        }
                        : blocker
                )
            );
        } catch (error) {
            console.error("Error deleting blocker:", error);
            console.error(
                "API response:",
                error?.response?.data
            );
        } finally {
            setDeletingBlockerId(null);
        }
    };

    const confirmDeleteBlocker = (blocker) => {
        confirmDialog({
            message: `Are you sure you want to delete "${blocker.title}"? This action cannot be undone.`,
            header: "Delete Blocker",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Delete",
            rejectLabel: "Cancel",
            acceptClassName: "p-button-danger",
            accept: () => handleDeleteBlocker(blocker)
        });
    };

    // =====================================================
    // TAB CHANGE
    // =====================================================

    const handleTabChange = async (event) => {
        const index = event.index;
        setActiveTab(index);

        if (index === 1 && !blockersLoaded) {
            await loadBlockers();
        }
    };

    // =====================================================
    // USERS / PROJECTS
    // =====================================================

    const searchUsers = (event) => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(async () => {
            try {
                const response = await getTeamMembers(
                    loggedUser.id,
                    false,
                    event.query,
                    1,
                    100
                );

                const userList = (response?.data || []).map((user) => ({
                    id: user.id,
                    name: user.fullName,
                    email: user.username
                }));

                setUsers(userList);
            } catch (error) {
                console.error("Error searching users:", error);
                setUsers([]);
            }
        }, 500);
    };

    const loadUserProjects = async (userId) => {
        if (!userId) {
            setProjects([]);
            return;
        }

        try {
            setLoadingProjects(true);

            const response = await getAgentProjectsRequest(
                userId,
                false,
                true,
                1,
                100
            );

            const projectList = (response?.data || [])
                .map((project) => ({
                    ...project,
                    name: getProjectName(project)
                }))
                .sort((a, b) => a.name.localeCompare(b.name));

            setProjects(projectList);
        } catch (error) {
            console.error("Error loading user projects:", error);
            setProjects([]);
        } finally {
            setLoadingProjects(false);
        }
    };

    const handleUserSelect = async (user) => {
        setSelectedUser(user);

        setNewCoaching((previous) => ({
            ...previous,
            coacheeId: user?.id || "",
            projectId: ""
        }));

        setProjects([]);

        if (user?.id) {
            await loadUserProjects(user.id);
        }
    };

    // =====================================================
    // COACHING ACTIONS
    // =====================================================

    const toggleItem = async (itemId) => {
        const isCurrentlyExpanded =
            expandedItems.includes(itemId);

        setExpandedItems((previous) =>
            isCurrentlyExpanded
                ? previous.filter((id) => id !== itemId)
                : [...previous, itemId]
        );

        if (!isCurrentlyExpanded) {
            await loadCoachingSessions(itemId);
        }
    };

    const handleStatusChange = async (item, newStatus) => {
        if (newStatus === item.status) return;

        try {
            setUpdatingId(item.id);

            const isReopening =
                item.status === "closed" &&
                newStatus !== "closed";

            const payload = isReopening
                ? {
                    status: newStatus
                }
                : {
                    coachId: item.coachId,
                    focusAreaId: item.focusAreaId,
                    title: item.title,
                    description: item.description,
                    followupDate: item.followupDate,
                    status: newStatus
                };

            await updateCoaching(item.id, payload);

            await loadCoachings(
                coachingPagination.page,
                coachingPagination.limit,
                selectedCoachingStatus
            );
        } catch (error) {
            console.error(
                "Error updating coaching status:",
                error
            );

            console.error(
                "API response:",
                error?.response?.data
            );
        } finally {
            setUpdatingId(null);
        }
    };

    // =====================================================
    // SESSION FORM
    // =====================================================

    const updateSessionField = (itemId, field, value) => {
        setSessionForms((previous) => ({
            ...previous,
            [itemId]: {
                ...previous[itemId],
                [field]: value
            }
        }));
    };

    const handleLogSession = async (itemId) => {
        const form = sessionForms[itemId] || {};

        const discussion = form.discussion?.trim();
        const actionItems = form.actionItems?.trim();

        if (!discussion) return;

        const payload = {
            discussion,
            actionItems: actionItems || "",
            status: "new"
        };

        try {
            setLoggingSessionId(itemId);

            await addCoachingSession(itemId, payload);

            setSessionForms((previous) => ({
                ...previous,
                [itemId]: {
                    discussion: "",
                    actionItems: ""
                }
            }));

            await loadCoachingSessions(itemId);
        } catch (error) {
            console.error(
                "Error creating coaching session:",
                error
            );

            console.error(
                "API response:",
                error?.response?.data
            );
        } finally {
            setLoggingSessionId(null);
        }
    };

    // =====================================================
    // CREATE COACHING
    // =====================================================

    const handleCreateCoaching = async (event) => {
        event.preventDefault();
        if (
            !newCoaching.title.trim() ||
            !newCoaching.description.trim() ||
            !newCoaching.coacheeId ||
            !newCoaching.projectId ||
            !newCoaching.focusAreaId
        ) {
            console.error(
                "Please fill all required fields."
            );

            return;
        }

        try {
            setCreating(true);

            const payload = {
                coacheeId: Number(newCoaching.coacheeId),
                coachId: Number(loggedUser.id),
                projectId: Number(newCoaching.projectId),
                focusAreaId: Number(newCoaching.focusAreaId),
                title: newCoaching.title.trim(),
                description: newCoaching.description.trim(),
                followupDate: newCoaching.followupDate
                    ? new Date(
                        newCoaching.followupDate
                    ).toISOString()
                    : null,
                status: "new"
            };

            await addCoaching(payload);

            setSelectedCoachingStatus("new");
            await loadCoachings(1, coachingPagination.limit, "new");

            setNewCoaching(INITIAL_COACHING);
            setSelectedUser(null);
            setProjects([]);
            setUsers([]);
            setShowCreateModal(false);
        } catch (error) {
            console.error(
                "Error creating coaching:",
                error
            );

            console.error(
                "API response:",
                error?.response?.data
            );
        } finally {
            setCreating(false);
        }
    };

    const updateNewCoachingField = (field, value) => {
        setNewCoaching((previous) => ({
            ...previous,
            [field]: value
        }));
    };

    const closeCreateModal = () => {
        setShowCreateModal(false);
        setNewCoaching(INITIAL_COACHING);
        setSelectedUser(null);
        setUsers([]);
        setProjects([]);
    };

    // =====================================================
    // BADGE HELPERS
    // =====================================================

    const getSeverityClass = (severity) => {
        switch (severity?.toLowerCase()) {
            case "low":
                return styles.lowSeverity;

            case "medium":
                return styles.mediumSeverity;

            case "high":
                return styles.highSeverity;

            case "urgent":
                return styles.urgentSeverity;

            default:
                return "";
        }
    };

    return (
        <div className={styles.page}>
            {/* =====================================================
                PAGE HEADER
            ===================================================== */}

            <div className={styles.growthHeader}>
                <h1>Growth Management</h1>

                <p>
                    Manage coachings and blockers.
                </p>
            </div>

            {/* =====================================================
                TABS
            ===================================================== */}

            <TabView
                activeIndex={activeTab}
                onTabChange={handleTabChange}
                className={styles.growthTabs}
            >
                {/* =====================================================
                    COACHINGS
                ===================================================== */}

                <TabPanel
                    header="Coachings"
                    leftIcon="pi pi-comments"
                >
                    <div className={styles.pageHeader}>
                        <div>
                            <div className={styles.titleRow}>
                                <h1>Coachings</h1>

                                <span className={styles.totalBadge}>
                                    {coachingPagination.totalResults} items
                                </span>
                            </div>

                            <p className={styles.pageDescription}>
                                Manage coaching items, track progress and
                                log coaching sessions.
                            </p>
                        </div>

                        <button
                            type="button"
                            className={styles.addButton}
                            onClick={() =>
                                setShowCreateModal(true)
                            }
                        >
                            <Plus size={17} />
                            Add coaching item
                        </button>
                    </div>

                    <div className={styles.coachingFilters}>

                        <SelectButton
                            value={selectedCoachingStatus}
                            options={COACHING_STATUS_OPTIONS}
                            onChange={(event) =>
                                handleCoachingStatusChange(event.value)
                            }
                            allowEmpty={false}
                            className={styles.coachingStatusButtons}
                            aria-label="Filter coachings by status"
                        />
                    </div>

                    <div className={styles.coachingList}>
                        {loading ? (
                            <div className={styles.emptySessions}>
                                <Loader2
                                    size={20}
                                    className={styles.spinner}
                                />

                                Loading coachings...
                            </div>
                        ) : coachingItems.length === 0 ? (
                            <div className={styles.emptySessions}>
                                No {formatStatus(selectedCoachingStatus).toLowerCase()} coachings found.
                            </div>
                        ) : (
                            coachingItems.map((item) => {
                                const isExpanded =
                                    expandedItems.includes(
                                        item.id
                                    );

                                const isClosed =
                                    item.status === "closed";

                                const isUpdating =
                                    updatingId === item.id;

                                const form =
                                    sessionForms[item.id] || {
                                        discussion: "",
                                        actionItems: ""
                                    };

                                return (
                                    <article
                                        key={item.id}
                                        className={
                                            styles.coachingCard
                                        }
                                    >
                                        <div
                                            className={
                                                styles.cardHeader
                                            }
                                            role="button"
                                            tabIndex={0}
                                            onClick={() =>
                                                toggleItem(
                                                    item.id
                                                )
                                            }
                                            onKeyDown={(
                                                event
                                            ) => {
                                                if (
                                                    event.key ===
                                                        "Enter" ||
                                                    event.key ===
                                                        " "
                                                ) {
                                                    toggleItem(
                                                        item.id
                                                    );
                                                }
                                            }}
                                        >
                                            <div
                                                className={
                                                    styles.cardHeaderContent
                                                }
                                            >
                                                <div
                                                    className={
                                                        styles.cardTitleLine
                                                    }
                                                >
                                                    <h2>
                                                        {item.title}
                                                    </h2>

                                                    <span
                                                        className={`${styles.statusBadge} ${
                                                            isClosed
                                                                ? styles.completedBadge
                                                                : styles.progressBadge
                                                        }`}
                                                    >
                                                        {formatStatus(
                                                            item.status
                                                        )}
                                                    </span>

                                                    {item.focusAreaName && (
                                                        <span
                                                            className={
                                                                styles.categoryBadge
                                                            }
                                                        >
                                                            {
                                                                item.focusAreaName
                                                            }
                                                        </span>
                                                    )}
                                                </div>

                                                <p
                                                    className={
                                                        styles.itemDescription
                                                    }
                                                >
                                                    {
                                                        item.description
                                                    }
                                                </p>

                                                <div
                                                    className={
                                                        styles.peopleRow
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            styles.person
                                                        }
                                                    >
                                                        <span
                                                            className={
                                                                styles.coachLabel
                                                            }
                                                        >
                                                            <UserRound
                                                                size={
                                                                    14
                                                                }
                                                            />
                                                            Coachee:
                                                        </span>

                                                        <span
                                                            className={`${styles.avatar} ${styles.userAvatar}`}
                                                        >
                                                            {getInitials(
                                                                item.coacheeName
                                                            )}
                                                        </span>

                                                        <span>
                                                            {
                                                                item.coacheeName
                                                            }
                                                        </span>
                                                    </div>

                                                    <div
                                                        className={
                                                            styles.person
                                                        }
                                                    >
                                                        <span
                                                            className={
                                                                styles.coachLabel
                                                            }
                                                        >
                                                            <UserRound
                                                                size={
                                                                    14
                                                                }
                                                            />
                                                            Coach:
                                                        </span>

                                                        <span
                                                            className={`${styles.avatar} ${styles.coachAvatar}`}
                                                        >
                                                            {getInitials(
                                                                item.coachName
                                                            )}
                                                        </span>

                                                        <span>
                                                            {
                                                                item.coachName
                                                            }
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div
                                                className={
                                                    styles.headerActions
                                                }
                                            >
                                                <select
                                                    className={
                                                        styles.statusSelect
                                                    }
                                                    value={
                                                        item.status
                                                    }
                                                    disabled={
                                                        isUpdating
                                                    }
                                                    onClick={(
                                                        event
                                                    ) =>
                                                        event.stopPropagation()
                                                    }
                                                    onChange={(
                                                        event
                                                    ) => {
                                                        event.stopPropagation();

                                                        handleStatusChange(
                                                            item,
                                                            event
                                                                .target
                                                                .value
                                                        );
                                                    }}
                                                >
                                                    <option value="new">
                                                        New
                                                    </option>

                                                    <option value="in-progress">
                                                        In Progress
                                                    </option>

                                                    <option value="closed">
                                                        Closed
                                                    </option>
                                                </select>

                                                {isUpdating && (
                                                    <Loader2
                                                        size={16}
                                                        className={
                                                            styles.spinner
                                                        }
                                                    />
                                                )}

                                                <button
                                                    type="button"
                                                    className={
                                                        styles.expandButton
                                                    }
                                                    onClick={(
                                                        event
                                                    ) => {
                                                        event.stopPropagation();

                                                        toggleItem(
                                                            item.id
                                                        );
                                                    }}
                                                >
                                                    {isExpanded ? (
                                                        <ChevronUp
                                                            size={
                                                                20
                                                            }
                                                        />
                                                    ) : (
                                                        <ChevronDown
                                                            size={
                                                                20
                                                            }
                                                        />
                                                    )}
                                                </button>
                                            </div>
                                        </div>

                                        {isExpanded && (
                                            <div
                                                className={
                                                    styles.cardContent
                                                }
                                            >
                                                <div
                                                    className={
                                                        styles.sessionsHeader
                                                    }
                                                >
                                                    <h3>
                                                        Sessions
                                                    </h3>

                                                    <span>
                                                        {item
                                                            .sessions
                                                            ?.length ||
                                                            0}{" "}
                                                        {item
                                                            .sessions
                                                            ?.length ===
                                                        1
                                                            ? "session"
                                                            : "sessions"}
                                                    </span>
                                                </div>

                                                {loadingSessions[
                                                    item.id
                                                ] ? (
                                                    <div
                                                        className={
                                                            styles.emptySessions
                                                        }
                                                    >
                                                        <Loader2
                                                            size={
                                                                18
                                                            }
                                                            className={
                                                                styles.spinner
                                                            }
                                                        />

                                                        Loading
                                                        sessions...
                                                    </div>
                                                ) : item.sessions
                                                      ?.length >
                                                  0 ? (
                                                    <div
                                                        className={
                                                            styles.sessionsList
                                                        }
                                                    >
                                                        {item.sessions.map(
                                                            (
                                                                session
                                                            ) => (
                                                                <div
                                                                    key={
                                                                        session.id
                                                                    }
                                                                    className={
                                                                        styles.sessionCard
                                                                    }
                                                                >
                                                                    <div
                                                                        className={
                                                                            styles.sessionTopRow
                                                                        }
                                                                    >
                                                                        <span
                                                                            className={
                                                                                styles.sessionDate
                                                                            }
                                                                        >
                                                                            {formatDate(
                                                                                session.createdAt
                                                                            )}
                                                                        </span>

                                                                        <span
                                                                            className={
                                                                                styles.sessionStatus
                                                                            }
                                                                        >
                                                                            {formatStatus(
                                                                                session.status
                                                                            )}
                                                                        </span>
                                                                    </div>

                                                                    <p
                                                                        className={
                                                                            styles.sessionDiscussion
                                                                        }
                                                                    >
                                                                        {
                                                                            session.discussion
                                                                        }
                                                                    </p>

                                                                    {session.actionItems && (
                                                                        <div
                                                                            className={
                                                                                styles.actionList
                                                                            }
                                                                        >
                                                                            {session.actionItems
                                                                                .split(
                                                                                    "\n"
                                                                                )
                                                                                .map(
                                                                                    (
                                                                                        action
                                                                                    ) =>
                                                                                        action.trim()
                                                                                )
                                                                                .filter(
                                                                                    Boolean
                                                                                )
                                                                                .map(
                                                                                    (
                                                                                        action,
                                                                                        index
                                                                                    ) => (
                                                                                        <div
                                                                                            key={`${session.id}-${index}`}
                                                                                            className={
                                                                                                styles.actionItem
                                                                                            }
                                                                                        >
                                                                                            <span>
                                                                                                →
                                                                                            </span>

                                                                                            <span>
                                                                                                {
                                                                                                    action
                                                                                                }
                                                                                            </span>
                                                                                        </div>
                                                                                    )
                                                                                )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div
                                                        className={
                                                            styles.emptySessions
                                                        }
                                                    >
                                                        No coaching
                                                        sessions
                                                        logged yet.
                                                    </div>
                                                )}

                                                {!isClosed && (
                                                    <div
                                                        className={
                                                            styles.sessionForm
                                                        }
                                                    >
                                                        <h4>
                                                            Log a
                                                            session
                                                        </h4>

                                                        <textarea
                                                            maxLength={100}
                                                            rows={
                                                                3
                                                            }
                                                            placeholder="What did you discuss?"
                                                            value={
                                                                form.discussion ||
                                                                ""
                                                            }
                                                            onChange={(
                                                                event
                                                            ) =>
                                                                updateSessionField(
                                                                    item.id,
                                                                    "discussion",
                                                                    event
                                                                        .target
                                                                        .value
                                                                )
                                                            }
                                                        />

                                                        <textarea
                                                            maxLength={255}
                                                            rows={
                                                                3
                                                            }
                                                            placeholder="Action items (one per line)"
                                                            value={
                                                                form.actionItems ||
                                                                ""
                                                            }
                                                            onChange={(
                                                                event
                                                            ) =>
                                                                updateSessionField(
                                                                    item.id,
                                                                    "actionItems",
                                                                    event
                                                                        .target
                                                                        .value
                                                                )
                                                            }
                                                        />

                                                        <div
                                                            className={
                                                                styles.sessionFormFooter
                                                            }
                                                        >
                                                            <span
                                                                className={
                                                                    styles.formHint
                                                                }
                                                            >
                                                                Add
                                                                one
                                                                action
                                                                item
                                                                per
                                                                line.
                                                            </span>

                                                            <button
                                                                type="button"
                                                                className={
                                                                    styles.logSessionButton
                                                                }
                                                                disabled={
                                                                    !form.discussion?.trim() ||
                                                                    loggingSessionId ===
                                                                        item.id
                                                                }
                                                                onClick={() =>
                                                                    handleLogSession(
                                                                        item.id
                                                                    )
                                                                }
                                                            >
                                                                {loggingSessionId ===
                                                                item.id ? (
                                                                    <>
                                                                        <Loader2
                                                                            size={
                                                                                14
                                                                            }
                                                                            className={
                                                                                styles.spinner
                                                                            }
                                                                        />
                                                                        Logging...
                                                                    </>
                                                                ) : (
                                                                    "Log session"
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </article>
                                );
                            })
                        )}
                    </div>

                    {coachingPagination.totalResults > 0 && (
                        <div className={styles.coachingPagination}>
                            <Paginator
                                first={
                                    (coachingPagination.page - 1) *
                                    coachingPagination.limit
                                }
                                rows={coachingPagination.limit}
                                totalRecords={coachingPagination.totalResults}
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                                onPageChange={handleCoachingPageChange}
                            />
                        </div>
                    )}
                </TabPanel>

                {/* =====================================================
                    BLOCKERS
                ===================================================== */}

                <TabPanel
                    header="Blockers"
                    leftIcon="pi pi-exclamation-triangle"
                >
                    <div className={styles.tabContent}>
                        <div className={styles.tabHeader}>
                            <div>
                                <div className={styles.titleRow}>
                                    <h1>Blockers</h1>
                                    <span className={styles.totalBadge}>{blockers.length} items</span>
                                </div>
                                <p className={styles.pageDescription}>Review blockers visible to you and monitor their SLA.</p>
                            </div>

                            <div className={styles.blockerStatusFilter}>
                                <label htmlFor="manager-blockers-status">Status:</label>
                                <Dropdown
                                    inputId="manager-blockers-status"
                                    value={selectedBlockerStatus}
                                    options={blockerStatusOptions}
                                    onChange={(event) => handleBlockerStatusChange(event.value)}
                                    placeholder="Select status"
                                    disabled={loadingBlockers}
                                    className={styles.blockerStatusDropdown}
                                />
                            </div>
                        </div>
                        {loadingBlockers ? (
                            <div className={styles.emptySessions}>
                                <Loader2 size={20} className={styles.spinner} />
                                Loading blockers...
                            </div>
                        ) : blockers.length === 0 ? (
                            <div className={styles.emptySessions}>
                                No blockers found.
                            </div>
                        ) : (
                            <div className={styles.blockerGrid}>
                                {blockers.map((blocker) => (
                                    <article
                                        key={blocker.id}
                                        className={styles.blockerCard}
                                    >
                                        <div className={styles.blockerCardTop}>
                                            <div className={styles.blockerHeading}>
                                                <h2>{blocker.title}</h2>

                                                <div className={styles.badgeRow}>
                                                    {blocker.severity && (
                                                        <span
                                                            className={`${styles.severityBadge} ${getSeverityClass(
                                                                blocker.severity
                                                            )}`}
                                                        >
                                                            {formatStatus(blocker.severity)}
                                                        </span>
                                                    )}

                                                    {blocker.ownerName && (
                                                        <span className={styles.ownerBadge}>
                                                            <i className="pi pi-user" />
                                                            {blocker.ownerName}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className={styles.blockerActions}>
                                                <Button
                                                    type="button"
                                                    label="Edit"
                                                    icon="pi pi-pencil"
                                                    className={styles.blockerEditButton}
                                                    onClick={() => openEditBlocker(blocker)}
                                                />

                                                <Button
                                                    type="button"
                                                    label="Delete"
                                                    icon="pi pi-trash"
                                                    className={styles.blockerDeleteButton}
                                                    loading={
                                                        deletingBlockerId === blocker.id
                                                    }
                                                    disabled={
                                                        deletingBlockerId === blocker.id
                                                    }
                                                    onClick={() =>
                                                        confirmDeleteBlocker(blocker)
                                                    }
                                                />
                                            </div>
                                        </div>

                                        <p className={styles.blockerDescription}>
                                            {blocker.description ||
                                                "No description provided."}
                                        </p>

                                        <div className={styles.blockerFooter}>
                                            <div className={styles.blockerMetaInline}>
                                                <span>
                                                    <strong>Created by</strong>
                                                    {blocker.createdByName || "-"}
                                                </span>

                                                <span>
                                                    <strong>Created</strong>
                                                    {formatDate(blocker.createdAt)}
                                                </span>

                                                {blocker.updatedByName && (
                                                    <span>
                                                        <strong>Updated by</strong>
                                                        {blocker.updatedByName}
                                                    </span>
                                                )}
                                            </div>

                                            {blocker.sla && (
                                                <div className={styles.slaInline}>
                                                    <i className="pi pi-clock" />
                                                    <span>
                                                        SLA {formatDateTime(blocker.sla)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </article>
                                ))}
                            </div>
                        )}
                    </div>
                </TabPanel>

            </TabView>

            {/* =====================================================
                CREATE COACHING MODAL
            ===================================================== */}

            {showCreateModal && (
                <div
                    className={styles.modalOverlay}
                    onMouseDown={closeCreateModal}
                >
                    <div
                        className={styles.modal}
                        onMouseDown={(event) =>
                            event.stopPropagation()
                        }
                    >
                        <div className={styles.modalHeader}>
                            <div>
                                <h2>Add coaching item</h2>

                                <p>
                                    Create a new coaching
                                    objective for a team member.
                                </p>
                            </div>

                            <button
                                type="button"
                                className={
                                    styles.closeButton
                                }
                                onClick={closeCreateModal}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <form
                            className={styles.modalForm}
                            onSubmit={handleCreateCoaching}
                        >
                            <div
                                className={
                                    styles.formGroup
                                }
                            >
                                <label htmlFor="coaching-title">
                                    Title
                                </label>

                                <input
                                    id="coaching-title"
                                    maxLength={100}
                                    type="text"
                                    placeholder="Ex: Improve written communication"
                                    value={
                                        newCoaching.title
                                    }
                                    onChange={(event) =>
                                        updateNewCoachingField(
                                            "title",
                                            event.target
                                                .value
                                        )
                                    }
                                />
                            </div>

                            <div
                                className={
                                    styles.formGroup
                                }
                            >
                                <label htmlFor="coaching-description">
                                    Description
                                </label>

                                <textarea
                                    id="coaching-description"
                                    rows={4}
                                    maxLength={255}
                                    placeholder="Describe the coaching goal and expected improvement..."
                                    value={
                                        newCoaching.description
                                    }
                                    onChange={(event) =>
                                        updateNewCoachingField(
                                            "description",
                                            event.target
                                                .value
                                        )
                                    }
                                />

                                <span
                                    className={
                                        styles.formHint
                                    }
                                >
                                    {
                                        newCoaching
                                            .description
                                            .length
                                    }
                                    /255
                                </span>
                            </div>

                            <div
                                className={
                                    styles.formGrid
                                }
                            >
                                <div
                                    className={
                                        styles.formGroup
                                    }
                                >
                                    <label htmlFor="coaching-coachee">
                                        Coachee
                                    </label>

                                    <AutoComplete
                                        id="coaching-coachee"
                                        value={
                                            selectedUser
                                        }
                                        suggestions={
                                            users
                                        }
                                        completeMethod={
                                            searchUsers
                                        }
                                        field="name"
                                        placeholder="Search team member..."
                                        forceSelection
                                        appendTo="self"
                                        delay={0}
                                        onChange={(
                                            event
                                        ) => {
                                            setSelectedUser(
                                                event.value
                                            );

                                            if (
                                                !event.value ||
                                                typeof event.value ===
                                                    "string"
                                            ) {
                                                setNewCoaching(
                                                    (
                                                        previous
                                                    ) => ({
                                                        ...previous,
                                                        coacheeId:
                                                            "",
                                                        projectId:
                                                            ""
                                                    })
                                                );

                                                setProjects(
                                                    []
                                                );
                                            }
                                        }}
                                        onSelect={(
                                            event
                                        ) =>
                                            handleUserSelect(
                                                event.value
                                            )
                                        }
                                    />
                                </div>

                                <div
                                    className={
                                        styles.formGroup
                                    }
                                >
                                    <label htmlFor="coaching-project">
                                        Project
                                    </label>

                                    <select
                                        id="coaching-project"
                                        value={
                                            newCoaching.projectId
                                        }
                                        disabled={
                                            !newCoaching.coacheeId ||
                                            loadingProjects
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            updateNewCoachingField(
                                                "projectId",
                                                event.target
                                                    .value
                                            )
                                        }
                                    >
                                        <option value="">
                                            {!newCoaching.coacheeId
                                                ? "Select a user first"
                                                : loadingProjects
                                                  ? "Loading projects..."
                                                  : "Select a project"}
                                        </option>

                                        {projects.map(
                                            (project) => (
                                                <option
                                                    key={
                                                        project.id
                                                    }
                                                    value={
                                                        project.id
                                                    }
                                                >
                                                    {
                                                        project.name
                                                    }
                                                </option>
                                            )
                                        )}
                                    </select>
                                </div>
                            </div>

                            <div
                                className={
                                    styles.formGrid
                                }
                            >
                                <div
                                    className={
                                        styles.formGroup
                                    }
                                >
                                    <label htmlFor="coaching-focus-area">
                                        Focus Area
                                    </label>
                                    <select onChange={(event) => (updateNewCoachingField("focusAreaId", event.target.value))} defaultValue={1}>
                                        {focusAreas.map(item => {
                                            return (
                                                <option key={item.id} value={item.id}>{item.name}</option>

                                            )
                                        })}
                                    </select>
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="coaching-followup">
                                        Follow-up date
                                    </label>

                                    <input
                                        id="coaching-followup"
                                        type="date"
                                        min={format(new Date(), "yyyy-MM-dd")}
                                        value={newCoaching.followupDate}
                                        onChange={(event) => updateNewCoachingField("followupDate",event.target .value)}
                                    />
                                </div>
                            </div>

                            <div
                                className={styles.modalFooter}
                            >
                                <button
                                    type="button"
                                    className={styles.cancelButton}
                                    onClick={closeCreateModal}
                                >
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    className={styles.createButton}
                                    disabled={creating}
                                >
                                    {creating ? (
                                        <Loader2 size={16} className={styles.spinner} />
                                    ) : (
                                        <Plus size={16} />
                                    )}

                                    {creating ? "Creating..." : "Create coaching item"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <Dialog
                header="Edit blocker"
                visible={showEditBlocker}
                className="blocker-dialog"
                modal
                draggable={false}
                onHide={closeEditBlocker}
            >
                <div className="summary-growth-modal">
                    <div className="summary-growth-field">
                        <label htmlFor="edit-blocker-title">
                            Title ({MAX_TITLE_LENGTH - blockerForm.title.length})
                        </label>

                        <InputText
                            id="edit-blocker-title"
                            value={blockerForm.title}
                            maxLength={100}
                            onChange={(event) =>
                                updateBlockerField(
                                    "title",
                                    event.target.value
                                )
                            }
                        />
                    </div>

                    <div className={styles.blockerFormRow}>
                        <div className="summary-growth-field">
                            <label htmlFor="edit-blocker-severity">Severity</label>
                            <Dropdown
                                inputId="edit-blocker-severity"
                                value={blockerForm.severity}
                                options={blockerSeverityOptions}
                                onChange={(e) => updateBlockerField("severity", e.value)}
                                placeholder="Select severity"
                                className="w-full"
                            />
                        </div>
                        <div className="summary-growth-field">
                            <label htmlFor="edit-blocker-status">Status</label>
                            <Dropdown
                                inputId="edit-blocker-status"
                                value={blockerForm.status}
                                options={[
                                    { label: "Open", value: "open" },
                                    { label: "In Progress", value: "in-progress" },
                                    { label: "Closed", value: "closed" }
                                ]}
                                onChange={(e) => updateBlockerField("status", e.value)}
                                placeholder="Select status"
                                className="w-full"
                            />
                        </div>
                    </div>

                    <div className="summary-growth-field">
                        <label htmlFor="edit-blocker-description">
                            Owner
                        </label>

                        <AutoComplete
                            id="blocker-owner"
                            value={blockerForm.ownerName}
                            suggestions={users}
                            completeMethod={searchUsers}
                            field="name"
                            forceSelection
                            appendTo="self"
                            delay={0}
                            placeholder="Search user..."
                            className="w-full"
                            inputClassName="w-full"
                            onChange={(e) => {
                                if (typeof e.value === "string") {
                                    setBlockerForm((previous) => ({
                                        ...previous,
                                        ownerName: e.value,
                                        ownerId: null
                                    }));
                                } else if (e.value) {
                                    setBlockerForm((previous) => ({
                                        ...previous,
                                        ownerName: e.value.name,
                                        ownerId: e.value.id
                                    }));
                                } else {
                                    setBlockerForm((previous) => ({
                                        ...previous,
                                        ownerName: "",
                                        ownerId: null
                                    }));
                                }
                            }}
                            onSelect={(e) => {
                                setBlockerForm((previous) => ({
                                    ...previous,
                                    ownerName: e.value.name,
                                    ownerId: e.value.id
                                }));
                            }}
                        />
                    </div>

                    <div className="summary-growth-field">
                        <label htmlFor="edit-blocker-description">
                            Description ({MAX_DESCRIPTION_LENGTH - blockerForm.description.length})
                        </label>

                        <InputTextarea
                            id="edit-blocker-description"
                            value={blockerForm.description}
                            rows={5}
                            maxLength={255}
                            autoResize
                            onChange={(event) =>
                                updateBlockerField(
                                    "description",
                                    event.target.value
                                )
                            }
                        />
                    </div>


                    <div className="summary-growth-modal-actions">
                        <Button
                            type="button"
                            label="Cancel"
                            severity="secondary"
                            text
                            disabled={savingBlocker}
                            onClick={closeEditBlocker}
                        />

                        <Button
                            type="button"
                            label="Save changes"
                            icon="pi pi-check"
                            loading={savingBlocker}
                            disabled={
                                !blockerForm.title.trim() ||
                                !blockerForm.description.trim() ||
                                !blockerForm.severity ||
                                !blockerForm.ownerId
                            }
                            onClick={handleUpdateBlocker}
                        />
                    </div>
                </div>
            </Dialog>

        </div>
    );
}

export default Coaching;